import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CONTACT } from '@/config/contact';
import Button from '../components/ui/Button';
import Seo from '@/components/Seo';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';

const CookiePolicyPage: React.FC = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const locale = language === 'en' ? 'en' : 'el';
  const meta = getPageMeta('cookie-policy', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <div className="container mx-auto max-w-4xl px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 rounded-2xl border border-white/15 bg-black/70 px-4 py-4 shadow-glass md:px-6 md:py-6">
          <Button
            as="button"
            onClick={() => window.navigateToHome?.()}
            variant="secondary"
            size="base"
            className="items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white transition-colors duration-200 hover:border-brand-highlight/60 hover:text-brand-highlight md:px-4 md:text-base"
          >
            <ArrowLeft className="h-5 w-5" />
            {language === 'en' ? 'Back' : 'Πίσω'}
          </Button>
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {language === 'en' ? 'Legal' : 'Νομικά'}
            </p>
            <h1 className="mt-2 text-3xl font-semibold text-white md:text-4xl">
              {language === 'en' ? 'Cookie Policy' : 'Πολιτική Cookies'}
            </h1>
          </div>
          <div className="w-16 md:w-20" />
        </div>

        {/* Content */}
        <div className="space-y-8 rounded-[32px] border border-white/15 bg-black/70 p-6 shadow-glass md:p-10">
          
          {/* Introduction */}
          <div>
            <p className="text-white/70 text-lg leading-relaxed mb-4">
              {language === 'en'
                ? 'This Cookie Policy explains how PixelPro Solutions uses cookies and similar technologies on its website to improve user experience and analyze traffic.'
                : 'Η παρούσα Πολιτική Cookies εξηγεί πώς η PixelPro Solutions χρησιμοποιεί cookies και παρόμοιες τεχνολογίες στον ιστότοπό της για να βελτιώσει την εμπειρία χρήστη και να αναλύσει την επισκεψιμότητα.'
              }
            </p>
            <p className="text-white/50 text-sm">
              <strong>{language === 'en' ? 'Last updated:' : 'Τελευταία ενημέρωση:'}</strong> {language === 'en' ? 'January 19, 2025' : '19 Ιανουαρίου 2025'}
            </p>
          </div>

          {/* Section 1 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '1. What are Cookies' : '1. Τι είναι τα Cookies'}
            </h2>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'Cookies are small text files that are stored on your device (computer, tablet, mobile) when you visit a website. They help the website "remember" your actions and preferences.'
                : 'Τα cookies είναι μικρά αρχεία κειμένου που αποθηκεύονται στη συσκευή σας (υπολογιστή, tablet, κινητό) όταν επισκέπτεστε έναν ιστότοπο. Βοηθούν τον ιστότοπο να "θυμάται" τις ενέργειές σας και τις προτιμήσεις σας.'
              }
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '2. Which Cookies We Use' : '2. Ποια Cookies Χρησιμοποιούμε'}
            </h2>
            
            <h3 className="text-xl font-semibold text-white mb-3">
              {language === 'en' ? '2.1 Google Analytics Cookies' : '2.1 Cookies Google Analytics'}
            </h3>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'We use Google Analytics to understand how visitors use our website. These cookies collect anonymous information such as:'
                : 'Χρησιμοποιούμε το Google Analytics για να κατανοήσουμε πώς οι επισκέπτες χρησιμοποιούν τον ιστότοπό μας. Αυτά τα cookies συλλέγουν ανώνυμες πληροφορίες όπως:'
              }
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4 mb-6">
              <li>{language === 'en' ? 'Number of visitors' : 'Αριθμός επισκεπτών'}</li>
              <li>{language === 'en' ? 'Pages they visit' : 'Σελίδες που επισκέπτονται'}</li>
              <li>{language === 'en' ? 'Time spent on website' : 'Χρόνος παραμονής στον ιστότοπο'}</li>
              <li>{language === 'en' ? 'Visit source (e.g. Google, direct visit)' : 'Πηγή επίσκεψης (π.χ. Google, άμεση επίσκεψη)'}</li>
              <li>{language === 'en' ? 'Geographic location (city level)' : 'Γεωγραφική τοποθεσία (σε επίπεδο πόλης)'}</li>
            </ul>

            <div className="overflow-x-auto">
              <table className="w-full overflow-hidden rounded-2xl border border-white/15 bg-black/80">
                <thead>
                  <tr className="bg-black/70">
                    <th className="border border-white/15 p-3 text-left text-white">
                      {language === 'en' ? 'Cookie Name' : 'Όνομα Cookie'}
                    </th>
                    <th className="border border-white/15 p-3 text-left text-white">
                      {language === 'en' ? 'Purpose' : 'Σκοπός'}
                    </th>
                    <th className="border border-white/15 p-3 text-left text-white">
                      {language === 'en' ? 'Duration' : 'Διάρκεια'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-white/15 p-3 text-white/70">_ga</td>
                    <td className="border border-white/15 p-3 text-white/70">
                      {language === 'en' ? 'Distinguishes unique users' : 'Διακρίνει μοναδικούς χρήστες'}
                    </td>
                    <td className="border border-white/15 p-3 text-white/70">
                      {language === 'en' ? '2 years' : '2 έτη'}
                    </td>
                  </tr>
                  <tr className="bg-black/70">
                    <td className="border border-white/15 p-3 text-white/70">_ga_*</td>
                    <td className="border border-white/15 p-3 text-white/70">
                      {language === 'en' ? 'Stores session state' : 'Αποθηκεύει κατάσταση συνεδρίας'}
                    </td>
                    <td className="border border-white/15 p-3 text-white/70">
                      {language === 'en' ? '2 years' : '2 έτη'}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-white/15 p-3 text-white/70">_gid</td>
                    <td className="border border-white/15 p-3 text-white/70">
                      {language === 'en' ? 'Distinguishes unique users' : 'Διακρίνει μοναδικούς χρήστες'}
                    </td>
                    <td className="border border-white/15 p-3 text-white/70">
                      {language === 'en' ? '24 hours' : '24 ώρες'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '3. Cookie Categories' : '3. Κατηγορίες Cookies'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-white/15 bg-black/60 p-6">
                <h4 className="font-bold text-white mb-3">
                  {language === 'en' ? 'Analytics Cookies' : 'Αναλυτικά Cookies'}
                </h4>
                <p className="text-white/70 text-sm mb-2">
                  <strong>{language === 'en' ? 'Purpose:' : 'Σκοπός:'}</strong> {language === 'en' ? 'Statistics and usage analysis' : 'Στατιστικές και ανάλυση χρήσης'}
                </p>
                <p className="text-white/70 text-sm mb-2">
                  <strong>{language === 'en' ? 'Provider:' : 'Πάροχος:'}</strong> Google Analytics
                </p>
                <p className="text-white/70 text-sm">
                  <strong>{language === 'en' ? 'Category:' : 'Κατηγορία:'}</strong> {language === 'en' ? 'Non-essential' : 'Μη απαραίτητα'}
                </p>
              </div>
              
              <div className="rounded-2xl border border-white/15 bg-black/60 p-6">
                <h4 className="font-bold text-white mb-3">
                  {language === 'en' ? 'Functional Cookies' : 'Λειτουργικά Cookies'}
                </h4>
                <p className="text-white/70 text-sm mb-2">
                  <strong>{language === 'en' ? 'Purpose:' : 'Σκοπός:'}</strong> {language === 'en' ? 'Basic website functionality' : 'Βασική λειτουργία ιστοτόπου'}
                </p>
                <p className="text-white/70 text-sm mb-2">
                  <strong>{language === 'en' ? 'Provider:' : 'Πάροχος:'}</strong> PixelPro Solutions
                </p>
                <p className="text-white/70 text-sm">
                  <strong>{language === 'en' ? 'Category:' : 'Κατηγορία:'}</strong> {language === 'en' ? 'Essential' : 'Απαραίτητα'}
                </p>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '4. Cookie Management' : '4. Διαχείριση Cookies'}
            </h2>
            
            <h3 className="text-xl font-semibold text-white mb-3">
              {language === 'en' ? '4.1 Browser Settings' : '4.1 Ρυθμίσεις Περιηγητή'}
            </h3>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'You can control and delete cookies through your browser settings:'
                : 'Μπορείτε να ελέγξετε και να διαγράψετε cookies μέσω των ρυθμίσεων του περιηγητή σας:'
              }
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4 mb-6">
              <li><strong>Chrome:</strong> {language === 'en' ? 'Settings → Privacy and security → Cookies' : 'Ρυθμίσεις → Απόρρητο και ασφάλεια → Cookies'}</li>
              <li><strong>Firefox:</strong> {language === 'en' ? 'Settings → Privacy and security → Cookies' : 'Ρυθμίσεις → Απόρρητο και ασφάλεια → Cookies'}</li>
              <li><strong>Safari:</strong> {language === 'en' ? 'Preferences → Privacy → Cookies' : 'Προτιμήσεις → Απόρρητο → Cookies'}</li>
              <li><strong>Edge:</strong> {language === 'en' ? 'Settings → Cookies and site permissions' : 'Ρυθμίσεις → Cookies και δικαιώματα ιστοτόπου'}</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">
              {language === 'en' ? '4.2 Opt-out Google Analytics' : '4.2 Opt-out Google Analytics'}
            </h3>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'You can opt out of Google Analytics by installing the '
                : 'Μπορείτε να εξαιρεθείτε από το Google Analytics εγκαθιστώντας το '
              }
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-pink-300 underline ml-1">
                Google Analytics Opt-out Browser Add-on
              </a>.
            </p>
          </div>

          {/* Section 5 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '5. Consent' : '5. Συναίνεση'}
            </h2>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'By continuing to use our website, you agree to the use of cookies as described in this policy. You can withdraw your consent at any time through your browser settings.'
                : 'Συνεχίζοντας να χρησιμοποιείτε τον ιστότοπό μας, συμφωνείτε με τη χρήση των cookies όπως περιγράφεται σε αυτή την πολιτική. Μπορείτε να ανακαλέσετε τη συναίνεσή σας ανά πάσα στιγμή μέσω των ρυθμίσεων του περιηγητή σας.'
              }
            </p>
          </div>

          {/* Section 6 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '6. Policy Changes' : '6. Αλλαγές στην Πολιτική'}
            </h2>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'We may update this Cookie Policy occasionally. Changes will be posted on this page with an updated date.'
                : 'Ενδέχεται να ενημερώσουμε αυτή την Πολιτική Cookies περιστασιακά. Οι αλλαγές θα δημοσιεύονται σε αυτή τη σελίδα με ενημερωμένη ημερομηνία.'
              }
            </p>
          </div>

          {/* Section 7 */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {language === 'en' ? '7. Contact' : '7. Επικοινωνία'}
            </h2>
            <p className="text-white/70 mb-4">
              {language === 'en'
                ? 'For questions about cookie usage, contact us:'
                : 'Για ερωτήσεις σχετικά με τη χρήση cookies, επικοινωνήστε μαζί μας:'
              }
            </p>
            <div className="rounded-2xl border border-white/15 bg-black/60 p-4">
              <p className="text-white/70 mb-2"><strong>Email:</strong> {CONTACT.email}</p>
              <p className="text-white/70 mb-2"><strong>{language === 'en' ? 'Phone UK:' : 'Τηλέφωνο UK:'}</strong> +44 20 8129 5829</p>
              <p className="text-white/70"><strong>{language === 'en' ? 'Phone GR:' : 'Τηλέφωνο GR:'}</strong> {CONTACT.phoneDisplay}</p>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-xl font-semibold text-white">
              {language === 'en' ? 'Questions about our cookie usage?' : 'Ερωτήσεις για τη χρήση cookies;'}
            </h3>
            <div className="rounded-2xl border border-white/15 bg-black/60 p-6">
              <p className="mb-4 text-white/70">
                {language === 'en'
                  ? 'Contact us for any questions about our privacy practices.'
                  : 'Επικοινωνήστε μαζί μας για οποιεσδήποτε ερωτήσεις σχετικά με τις πρακτικές απορρήτου μας.'
                }
              </p>
              <div className="rounded-2xl border border-white/15 bg-black/70 p-4">
                <p className="text-sm leading-relaxed text-white/70">
                  "Αυτός ο ιστότοπος χρησιμοποιεί cookies για να βελτιώσει την εμπειρία σας και να αναλύσει την επισκεψιμότητα μέσω Google Analytics. 
                  Τα δεδομένα συλλέγονται ανώνυμα και δεν μοιράζονται με τρίτους. Συνεχίζοντας την περιήγηση, συμφωνείτε με τη χρήση cookies. 
                  <button className='ml-1 text-white/70 underline transition-colors hover:text-brand-highlight'>Μάθετε περισσότερα</button> | 
                  <button className='ml-1 text-white/70 underline transition-colors hover:text-brand-highlight'>Ρυθμίσεις</button>"
                </p>
              </div>
              <Button
                as="button"
                variant="secondary"
                size="base"
                className="mt-4 w-full justify-center rounded-full border border-white/15 bg-white/5 text-white transition-colors hover:border-brand-highlight/60 hover:text-brand-highlight"
                onClick={() => window.navigateToConsultation?.()}
                trackCta={{
                  text: language === 'en' ? 'Contact Us' : 'Επικοινωνήστε μαζί μας',
                  section: 'cookie_policy_contact',
                  variant: 'secondary'
                }}
              >
                {language === 'en' ? 'Contact Us' : 'Επικοινωνήστε μαζί μας'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
